import { useState, useEffect } from 'react';
import { Plus, Trash2, GripVertical, ToggleLeft, ToggleRight } from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Modal from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

interface RuleCondition {
  field: string;
  operator: string;
  value: string;
}

interface RuleAction {
  type: string;
  value: string;
}

interface FulfillmentRule {
  id: string;
  name: string;
  priority: number;
  is_active: boolean;
  conditions: RuleCondition[];
  actions: RuleAction[];
}

const CONDITION_FIELDS = [
  { label: 'Order Total', value: 'order_total' },
  { label: 'Shipping State', value: 'shipping_state' },
  { label: 'Shipping Country', value: 'shipping_country' },
  { label: 'Total Weight (oz)', value: 'total_weight' },
  { label: 'Item Count', value: 'item_count' },
];

const OPERATORS = [
  { label: '=', value: 'eq' },
  { label: '!=', value: 'neq' },
  { label: '>', value: 'gt' },
  { label: '>=', value: 'gte' },
  { label: '<', value: 'lt' },
  { label: '<=', value: 'lte' },
  { label: 'in', value: 'in' },
];

const ACTION_TYPES = [
  { label: 'Assign Carrier', value: 'assign_carrier' },
  { label: 'Assign Service', value: 'assign_service' },
  { label: 'Add Insurance', value: 'add_insurance' },
  { label: 'Require Signature', value: 'require_signature' },
];

export default function FulfillmentRules() {
  const [rules, setRules] = useState<FulfillmentRule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState<FulfillmentRule | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [priority, setPriority] = useState('0');
  const [conditions, setConditions] = useState<RuleCondition[]>([{ field: 'order_total', operator: 'gt', value: '' }]);
  const [actions, setActions] = useState<RuleAction[]>([{ type: 'assign_carrier', value: '' }]);

  useEffect(() => { fetchRules(); }, []);

  async function fetchRules() {
    setIsLoading(true);
    try {
      const res = await api.get<{ rules: FulfillmentRule[] }>('/admin/fulfillment/rules');
      setRules(res.rules || []);
    } catch { /* ignore - table might not have data yet */ }
    finally { setIsLoading(false); }
  }

  function openNew() {
    setEditingRule(null);
    setName('');
    setPriority('0');
    setConditions([{ field: 'order_total', operator: 'gt', value: '' }]);
    setActions([{ type: 'assign_carrier', value: '' }]);
    setShowModal(true);
  }

  function openEdit(rule: FulfillmentRule) {
    setEditingRule(rule);
    setName(rule.name);
    setPriority(rule.priority.toString());
    setConditions(rule.conditions.length ? rule.conditions : [{ field: 'order_total', operator: 'gt', value: '' }]);
    setActions(rule.actions.length ? rule.actions : [{ type: 'assign_carrier', value: '' }]);
    setShowModal(true);
  }

  async function handleSave() {
    setIsSaving(true);
    try {
      const body = { name, priority: parseInt(priority), conditions, actions, is_active: editingRule?.is_active ?? true };
      if (editingRule) {
        await api.put(`/admin/fulfillment/rules/${editingRule.id}`, body);
      } else {
        await api.post('/admin/fulfillment/rules', body);
      }
      setShowModal(false);
      fetchRules();
    } catch (err: any) {
      alert(err.message || 'Failed to save rule');
    } finally { setIsSaving(false); }
  }

  async function toggleActive(rule: FulfillmentRule) {
    try {
      await api.put(`/admin/fulfillment/rules/${rule.id}`, { ...rule, is_active: !rule.is_active });
      fetchRules();
    } catch { /* ignore */ }
  }

  async function deleteRule(id: string) {
    if (!confirm('Delete this rule?')) return;
    try {
      await api.delete(`/admin/fulfillment/rules/${id}`);
      fetchRules();
    } catch { /* ignore */ }
  }

  if (isLoading) return <div className="flex justify-center py-12"><LoadingSpinner /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Fulfillment Rules</h1>
        <Button variant="primary" onClick={openNew}><Plus className="h-4 w-4 mr-1" />Add Rule</Button>
      </div>

      <p className="text-sm text-gray-500">
        Rules are evaluated in priority order (highest first) when creating shipments. Matching rules auto-recommend carrier and service.
      </p>

      {rules.length === 0 ? (
        <Card padding="lg">
          <div className="text-center py-8">
            <p className="text-gray-400 mb-4">No fulfillment rules configured yet.</p>
            <Button variant="primary" onClick={openNew}><Plus className="h-4 w-4 mr-1" />Create First Rule</Button>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {rules.sort((a, b) => b.priority - a.priority).map(rule => (
            <Card key={rule.id}>
              <div className="flex items-center gap-4">
                <GripVertical className="h-4 w-4 text-gray-300 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-gray-900">{rule.name}</h3>
                    <span className="text-xs text-gray-400">Priority: {rule.priority}</span>
                    {!rule.is_active && <span className="text-xs text-red-500">(disabled)</span>}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {rule.conditions.map((c, i) => (
                      <span key={i}>{i > 0 && ' AND '}{c.field} {c.operator} {c.value}</span>
                    ))}
                    {' -> '}
                    {rule.actions.map((a, i) => (
                      <span key={i}>{i > 0 && ', '}{a.type}: {a.value}</span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => toggleActive(rule)} title={rule.is_active ? 'Disable' : 'Enable'}>
                    {rule.is_active
                      ? <ToggleRight className="h-5 w-5 text-green-600" />
                      : <ToggleLeft className="h-5 w-5 text-gray-400" />}
                  </button>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(rule)}>Edit</Button>
                  <button onClick={() => deleteRule(rule.id)} className="text-red-400 hover:text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Rule Editor Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editingRule ? 'Edit Rule' : 'New Rule'}>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto">
          <Input label="Rule Name" value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Heavy packages use FedEx" />
          <Input label="Priority" type="number" value={priority} onChange={e => setPriority(e.target.value)} helperText="Higher = evaluated first" />

          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Conditions (all must match)</h4>
            {conditions.map((cond, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <Select value={cond.field} onChange={e => { const c = [...conditions]; c[i].field = e.target.value; setConditions(c); }}
                  options={CONDITION_FIELDS} />
                <Select value={cond.operator} onChange={e => { const c = [...conditions]; c[i].operator = e.target.value; setConditions(c); }}
                  options={OPERATORS} />
                <input value={cond.value} onChange={e => { const c = [...conditions]; c[i].value = e.target.value; setConditions(c); }}
                  className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm" placeholder="Value" />
                {conditions.length > 1 && (
                  <button onClick={() => setConditions(conditions.filter((_, j) => j !== i))} className="text-red-400">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            <Button size="sm" variant="ghost" onClick={() => setConditions([...conditions, { field: 'order_total', operator: 'gt', value: '' }])}>
              <Plus className="h-3 w-3 mr-1" />Add Condition
            </Button>
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Actions</h4>
            {actions.map((action, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <Select value={action.type} onChange={e => { const a = [...actions]; a[i].type = e.target.value; setActions(a); }}
                  options={ACTION_TYPES} />
                <input value={action.value} onChange={e => { const a = [...actions]; a[i].value = e.target.value; setActions(a); }}
                  className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm" placeholder="Value (e.g., usps, fedex_FEDEX_GROUND)" />
                {actions.length > 1 && (
                  <button onClick={() => setActions(actions.filter((_, j) => j !== i))} className="text-red-400">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            <Button size="sm" variant="ghost" onClick={() => setActions([...actions, { type: 'assign_carrier', value: '' }])}>
              <Plus className="h-3 w-3 mr-1" />Add Action
            </Button>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} isLoading={isSaving} disabled={!name.trim()}>Save Rule</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
